import _ from 'lodash'
import { createAction, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import expertiseModel, { Expertise, CreateExpertiseDto, ExpertiseState, UpdateExpertiseDto, ExpertiseService } from '@/core/models/expertise.model'
import { SearchRequestOptions } from '@/core/types/request'

import { RootState } from '@/redux/rootReducer'
import { Act } from '@/core/models/consultation.model'

export type ExpertiseReducerState = ExpertiseState

export const expertiseAdapter = createEntityAdapter({ selectId: (expertise: Expertise) => expertise._id })
const expertiseSelector = expertiseAdapter.getSelectors((state: RootState) => state.expertise.expertises)

export const selectExpertises = expertiseSelector.selectAll
export const selectExpertiseById = expertiseSelector.selectById

export const selectNewlyCreatedExpertiseId = (state: RootState) => state.expertise.newlyCreatedItemId
export const selectExpertiseSearchParams = (state: RootState) => state.expertise.search.params
export const selectExpertiseSearchResult = (state: RootState) => state.expertise.search.result

export const selectExpertiseGetRequestTracker = (state: RootState) => state.expertise.requestTracker.get
export const selectExpertiseCreateRequestTracker = (state: RootState) => state.expertise.requestTracker.create
export const selectExpertiseUpdateRequestTracker = (state: RootState) => state.expertise.requestTracker.update
export const selectExpertiseDeleteRequestTracker = (state: RootState) => state.expertise.requestTracker.delete

const initialState: ExpertiseReducerState = _.merge({}, expertiseModel.getInitialState(), {})

const expertiseSlice = createSlice({
  name: expertiseModel.reducerName,
  initialState,
  reducers: {
    createExpertise(state, action: PayloadAction<{ expertise: CreateExpertiseDto }>) {
      state.requestTracker.create.loading = true
    },
    createExpertiseSuccess(state, action: PayloadAction<Expertise>) {
      expertiseAdapter.addOne(state.expertises, action.payload)
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
      state.newlyCreatedItemId = action.payload._id
    },
    createExpertiseFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    createExternalServiceExpertise: {
      reducer: (state, action: PayloadAction<{ id: string; externalService: Exclude<ExpertiseService, ExpertiseService.MedConnect> }>) => {
        state.requestTracker.create.loading = true
      },
      prepare: (id: string, externalService: Exclude<ExpertiseService, ExpertiseService.MedConnect>) => ({
        payload: { id, externalService },
      }),
    },
    createExternalServiceExpertiseSuccess(state, action: PayloadAction<Expertise>) {
      expertiseAdapter.setOne(state.expertises, action.payload)
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createExternalServiceExpertiseFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    getExpertise(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
      state.newlyCreatedItemId = undefined
    },
    getExpertiseSuccess(state, action) {
      expertiseAdapter.upsertOne(state.expertises, action.payload)
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getExpertiseFailure(state, action) {
      state.requestTracker.get.loading = false
      state.requestTracker.get.error = action.payload
    },
    getExpertises: {
      reducer: (state, action: PayloadAction<{ query?: Record<string, any>; options?: SearchRequestOptions }>) => {
        state.search.params.query = action.payload.query
        state.search.params.options = action.payload.options
        state.requestTracker.list.loading = true
        state.newlyCreatedItemId = undefined
      },
      prepare: (query?: Record<string, any>, options?: SearchRequestOptions) => ({
        payload: {
          query,
          options,
        },
      }),
    },
    getExpertisesSuccess(state, action: PayloadAction<{ items: Expertise[]; totalCount: number }>) {
      const { items, totalCount } = action.payload
      expertiseAdapter.setAll(state.expertises, items)
      state.search.result.totalCount = totalCount
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    getExpertisesFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },
    updateExpertise: {
      reducer: (state, action: PayloadAction<{ expertiseId: string; changes: UpdateExpertiseDto }>) => {
        state.requestTracker.update.loading = true
        state.newlyCreatedItemId = undefined
      },
      prepare: (expertiseId: string, changes: UpdateExpertiseDto) => ({
        payload: {
          expertiseId,
          changes,
        },
      }),
    },
    updateExpertiseSuccess(state, action: PayloadAction<Expertise>) {
      const expertise = action.payload
      expertiseAdapter.updateOne(state.expertises, { id: expertise._id, changes: expertise })
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateExpertiseFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    deleteExpertise(state, action: PayloadAction<string>) {
      state.requestTracker.delete.loading = true
      state.newlyCreatedItemId = undefined
    },
    deleteExpertiseSuccess(state, action: PayloadAction<string>) {
      expertiseAdapter.removeOne(state.expertises, action.payload)
      state.requestTracker.delete.error = null
      state.requestTracker.delete.loading = false
    },
    deleteExpertiseFailure(state, action) {
      state.requestTracker.delete.error = action.payload
      state.requestTracker.delete.loading = false
    },
    generateFSP: {
      reducer: (state, action: PayloadAction<{ expertiseId: string; acts: Act[] }>) => {
        state.requestTracker.create.loading = true
      },
      prepare: (expertiseId: string, acts: Act[]) => ({
        payload: { expertiseId, acts },
      }),
    },
    generateFSPSuccess(state) {
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    generateFSPFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
  },
})

export const joinExpertiseSocketEvent = createAction<string>('expertise/joinExpertiseSocketEvent')
export const leaveExpertiseSocketEvent = createAction<string>('expertise/leaveExpertiseSocketEvent')

export const {
  createExpertise,
  createExpertiseSuccess,
  createExpertiseFailure,
  createExternalServiceExpertise,
  createExternalServiceExpertiseSuccess,
  createExternalServiceExpertiseFailure,
  getExpertise,
  getExpertiseSuccess,
  getExpertiseFailure,
  getExpertises,
  getExpertisesSuccess,
  getExpertisesFailure,
  updateExpertise,
  updateExpertiseSuccess,
  updateExpertiseFailure,
  deleteExpertise,
  deleteExpertiseSuccess,
  deleteExpertiseFailure,
  generateFSP,
  generateFSPSuccess,
  generateFSPFailure,
} = expertiseSlice.actions

export default expertiseSlice.reducer
